import { format, isValid, parse, addMinutes, getHours, getMinutes } from 'date-fns';

/**
 * Date and Time Formatting Utilities for Time Entry Forms
 * Story #25310: Support for localDateTime format
 */

/**
 * Formats a Date object as YYYY-MM-DD string for use as a grouping key
 * Story #41977: N+1 API call fix - batch entries grouping
 * @param {Date} date - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function formatDateKey(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

/**
 * Extracts date in YYYY-MM-DD format using local timezone
 * @param {Date|string} dateValue - Date to extract
 * @returns {string|null} Date string in YYYY-MM-DD format or null
 */
export function extractDate(dateValue) {
  if (!dateValue) return null;
  try {
    const d = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!isValid(d)) return null;
    // Use local date components to avoid UTC timezone shift
    return format(d, 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

/**
 * Extracts time in HH:mm:ss format
 * @param {Date|string} timeValue - Time to extract (can be Date object or HH:mm string)
 * @returns {string|null} Time string in HH:mm:ss format or null
 */
export function extractTime(timeValue) {
  if (!timeValue) return null;

  // If the value is already in HH:MM:SS string format, return as-is
  if (typeof timeValue === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue;
  }

  // If the value is in HH:MM string format, append seconds
  if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue + ':00';
  }

  // Otherwise convert from date object
  try {
    const date = typeof timeValue === 'string' ? new Date(timeValue) : timeValue;
    if (!isValid(date)) return null;
    return format(date, 'HH:mm:ss');
  } catch {
    return null;
  }
}

/**
 * Helper: Parses a time string (HH:mm or HH:mm:ss) into a Date object
 * @param {string} timeString - Time string to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
function parseTimeToDate(timeString) {
  if (!timeString || typeof timeString !== 'string') return null;

  // Normalize to HH:mm:ss format
  const normalized = timeString.length === 5 ? `${timeString}:00` : timeString;
  const date = parse(normalized, 'HH:mm:ss', new Date());
  return isValid(date) ? date : null;
}

/**
 * Parses time string (HH:mm:ss or HH:mm) into hours and minutes
 * Story #41530: Time Planner calendar positioning
 * @param {string} timeString - Time string in HH:mm:ss or HH:mm format
 * @returns {{hours: number, minutes: number}|null} Object with hours and minutes or null
 */
export function parseTimeString(timeString) {
  const date = parseTimeToDate(timeString);
  if (!date) return null;
  const hours = getHours(date);
  const minutes = getMinutes(date);

  // Validate ranges
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return {
    hours,
    minutes
  };
}

/**
 * Parses entry's localDateTime or fallback to startTime Date object
 * Story #41530: Time Planner calendar positioning
 * @param {Object} entry - Time entry with localDateTime or startTime
 * @param {boolean} useLocalDateTime - Whether to try localDateTime format (default: true)
 * @returns {{hours: number, minutes: number}|null} Object with hours and minutes or null
 */
export function parseEntryTime(entry, useLocalDateTime = true) {
  if (!entry) return null;

  // Try new localDateTime format first if feature flag enabled
  if (useLocalDateTime && entry.localDateTime?.time) {
    return parseTimeString(entry.localDateTime.time);
  }

  // Fallback to legacy Date format
  if (entry.startTime) {
    const date = new Date(entry.startTime);
    if (isValid(date)) {
      return {
        hours: getHours(date),
        minutes: getMinutes(date)
      };
    }
  }
  return null;
}

/**
 * Parses entry time into total minutes (for overlap detection and positioning)
 * Story #41530: Time Planner calendar positioning
 * @param {Object} entry - Time entry with localDateTime or startTime
 * @param {boolean} useLocalDateTime - Whether to try localDateTime format (default: true)
 * @returns {number} Total minutes from midnight (0 if invalid)
 */
export function parseEntryTimeInMinutes(entry, useLocalDateTime = true) {
  const timeData = parseEntryTime(entry, useLocalDateTime);
  if (!timeData) return 0;
  return timeData.hours * 60 + timeData.minutes;
}

/**
 * Gets timestamp from entry for sorting (supports both formats)
 * Story #41530: Time Planner calendar positioning
 * @param {Object} entry - Time entry with localDateTime or startTime
 * @param {boolean} useLocalDateTime - Whether to try localDateTime format (default: true)
 * @returns {number|null} Timestamp in milliseconds or null
 */
export function getEntryTimestamp(entry, useLocalDateTime = true) {
  if (!entry) return null;

  // Try new localDateTime format first if feature flag enabled
  if (useLocalDateTime && entry.localDateTime?.date && entry.localDateTime?.time) {
    const dateTimeStr = `${entry.localDateTime.date}T${entry.localDateTime.time}`;
    const date = new Date(dateTimeStr);
    if (isValid(date)) {
      return date.getTime();
    }
  }

  // Fallback to legacy Date format
  if (entry.startTime) {
    const date = new Date(entry.startTime);
    if (isValid(date)) {
      return date.getTime();
    }
  }
  return null;
}

/**
 * Story #41530: Format time string from 24h format (HH:mm:ss) to 12h format (h:mm a)
 * @param {string} timeString - Time in 24h format (e.g., "10:30:00" or "14:45:00")
 * @returns {string} Time in 12h format (e.g., "10:30 AM" or "2:45 PM")
 */
export function formatTimeFrom24h(timeString) {
  if (!timeString) return '';
  const date = parseTimeToDate(timeString);
  if (!date) return '';
  return format(date, 'h:mm a');
}

/**
 * Story #41530: Calculate end time from start time string and hours duration
 * @param {string} startTimeString - Start time in 24h format (e.g., "10:30:00")
 * @param {number} hours - Duration in hours (e.g., 1.5 for 1h 30m)
 * @returns {string} End time in 24h format (e.g., "15:30:00")
 */
export function calculateEndTimeFromHours(startTimeString, hours) {
  if (!startTimeString || hours == null) return startTimeString;
  const startDate = parseTimeToDate(startTimeString);
  if (!startDate) return startTimeString;
  const durationMinutes = Math.round(hours * 60);
  const endDate = addMinutes(startDate, durationMinutes);

  // Handle day overflow by using modulo for hours
  const endHours = getHours(endDate) % 24;
  const endMinutes = getMinutes(endDate);
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00`;
}

/**
 * Story #41530: Calculate end time from start time string and hours duration
 * @param {string} startTimeString - Start time in 24h format (e.g., "10:30:00")
 * @param {number} hours - Duration in hours (e.g., 5)
 * @returns {string} End time in 12h format (e.g., "3:30 PM")
 */
export function calculateEndTimeFrom24h(startTimeString, hours) {
  if (!startTimeString) return '';
  const startDate = parseTimeToDate(startTimeString);
  if (!startDate) return '';
  const durationMinutes = Math.round((hours || 0) * 60);
  const endDate = addMinutes(startDate, durationMinutes);
  return format(endDate, 'h:mm a');
}

/**
 * Story #41530: Format Date object to 12h time string (h:mm a)
 * @param {Date|string} dateOrString - Date object or ISO date string
 * @returns {string} Time in 12h format (e.g., "10:30 AM")
 */
export function formatTimeFromDate(dateOrString) {
  if (!dateOrString) return '';
  const d = new Date(dateOrString);
  if (!isValid(d)) return '';
  return format(d, 'h:mm a');
}

/**
 * Story #41530: Get formatted start time for an entry
 * Uses localDateTime when dateHandling feature is enabled, otherwise falls back to startTime
 * @param {Object} entry - Time entry object with startTime and optionally localDateTime
 * @param {boolean} useDateHandling - Whether the dateHandling feature flag is enabled
 * @returns {string} Formatted start time (e.g., "10:30 AM")
 */
export function getEntryStartTime(entry, useDateHandling) {
  if (!entry) return '';

  // Use localDateTime.time when feature flag enabled and data available
  if (useDateHandling && entry.localDateTime?.time) {
    return formatTimeFrom24h(entry.localDateTime.time);
  }

  // Fallback to original startTime
  return formatTimeFromDate(entry.startTime);
}

/**
 * Story #41530: Get formatted end time for an entry
 * Calculates from localDateTime + hours when dateHandling feature is enabled, otherwise falls back to endTime
 * @param {Object} entry - Time entry object with endTime, hours, and optionally localDateTime
 * @param {boolean} useDateHandling - Whether the dateHandling feature flag is enabled
 * @returns {string} Formatted end time (e.g., "3:30 PM")
 */
export function getEntryEndTime(entry, useDateHandling) {
  if (!entry) return '';

  // Use localDateTime.time + hours when feature flag enabled and data available
  if (useDateHandling && entry.localDateTime?.time) {
    return calculateEndTimeFrom24h(entry.localDateTime.time, entry.hours);
  }

  // Fallback to original endTime
  return formatTimeFromDate(entry.endTime);
}

/**
 * Story #41530: Create Date object from localDateTime.time string for a given date
 * Used for populating time pickers in edit forms
 * @param {string} dateString - Base date string (e.g., "2023-06-15" or Date object)
 * @param {string} timeString - Time in 24h format (e.g., "10:30:00")
 * @returns {Date} Date object with the specified time
 */
export function createDateFromLocalTime(dateString, timeString) {
  if (!dateString || !timeString) return null;

  // Handle both Date objects and date strings
  const dateStr = dateString instanceof Date ? format(dateString, 'yyyy-MM-dd') : dateString;

  // Normalize time to HH:mm:ss (strip milliseconds if present, e.g. "15:15:12.253" → "15:15:12")
  let normalizedTime = timeString.split('.')[0];
  if (normalizedTime.length === 5) normalizedTime = `${normalizedTime}:00`;

  // Parse combined date and time
  const dateTimeStr = `${dateStr} ${normalizedTime}`;
  const result = parse(dateTimeStr, 'yyyy-MM-dd HH:mm:ss', new Date());
  return isValid(result) ? result : null;
}

/**
 * Story #41530: Calculate end Date from start Date and hours duration
 * Used for populating end time pickers in edit forms
 * @param {Date} startDate - Start time as Date object
 * @param {number} hours - Duration in hours (e.g., 5)
 * @returns {Date} End time as Date object
 */
export function calculateEndDateFromHours(startDate, hours) {
  if (!startDate || !isValid(startDate)) return null;
  const durationMinutes = Math.round((hours || 0) * 60);
  return addMinutes(startDate, durationMinutes);
}

/**
 * Story #41530: Get start and end Date objects for an entry
 * Uses localDateTime when dateHandling feature is enabled, otherwise uses startTime/endTime
 * @param {Object} entry - Time entry object with date, startTime, endTime, hours, and optionally localDateTime
 * @param {boolean} useDateHandling - Whether the dateHandling feature flag is enabled
 * @returns {{startTime: Date, endTime: Date}} Object with startTime and endTime Date objects
 */
export function getEntryTimeDates(entry, useDateHandling) {
  if (!entry) return {
    startTime: null,
    endTime: null
  };

  // When dateHandling feature is enabled and localDateTime exists,
  // construct Date objects from localDateTime values to show actual local times
  if (useDateHandling && entry.localDateTime?.time) {
    // Use localDateTime.date (simple "YYYY-MM-DD" format), not entry.date (ISO string)
    const dateToUse = entry.localDateTime.date;
    const startTime = createDateFromLocalTime(dateToUse, entry.localDateTime.time);
    const endTime = calculateEndDateFromHours(startTime, entry.hours);
    return {
      startTime,
      endTime
    };
  }

  // Fallback to original startTime/endTime values (can be Date objects or ISO strings)
  const startTime = entry.startTime ? new Date(entry.startTime) : null;
  const endTime = entry.endTime ? new Date(entry.endTime) : null;

  // Validate that we got valid dates using date-fns isValid
  return {
    startTime: startTime && isValid(startTime) ? startTime : null,
    endTime: endTime && isValid(endTime) ? endTime : null
  };
}